import React, { ReactNode } from 'react';

import { seq, pass } from '../../utils/functional';

import { OMNI } from '../../constants/types';
import { BANNER_SEVERITIES } from '../../constants/banner';

import { removeLinks, linkMatcher } from '../../utils/format';

type BannerSeverity = (typeof BANNER_SEVERITIES)[number];

type StatusBannerAction = string;

export interface StatusBannerBanner {
    severity: BannerSeverity;
    message: ReactNode;
    persistent?: boolean;
    action?: StatusBannerAction;
    args?: unknown[];
    content?: ReactNode;
    isDynamicMessage?: boolean;
    displayCloseBtn?: boolean;
    [key: string]: unknown;
}

export interface StatusBannerProps {
    banner?: StatusBannerBanner;
    closeCB?: () => void;
    hasSidebar?: boolean;
    dispatch?: (...args: unknown[]) => void;
    className?: string;
    isOnConf?: boolean;
    id?: string;
}

const setAction = (
    action: StatusBannerAction | undefined,
    args: unknown[] | undefined,
    dispatch?: (...args: unknown[]) => void
): (() => void) => {
    if (action === OMNI && dispatch) {
        const safeArgs = args ?? [];

        return () => {
            dispatch(...safeArgs);
        };
    }

    return pass;
};

export const linkify = (value: string): ReactNode[] => {
    const substs: Record<string, ReactNode> = {};
    let text = value;

    linkMatcher.lastIndex = 0;

    while (true) {
        const match = linkMatcher.exec(text);

        if (!match) {
            break;
        }

        substs[match[0]] = (
            <a href={match[2]} target='_blank' rel='noopener noreferrer' key={match[1]}>
                {match[1]}
            </a>
        );
    }

    const result: ReactNode[] = [];

    Object.keys(substs).forEach((subst, index) => {
        const [before, after] = text.split(subst, 2);
        result.push(before, <span key={`sp${index}`}>&nbsp;</span>, substs[subst]);
        text = after;
    });

    if (result.length) {
        result.push(<span key='spfin'>&nbsp;</span>, text);
    } else {
        result.push(text);
    }

    return result;
};

const StatusBanner = ({
    banner,
    closeCB,
    hasSidebar,
    dispatch,
    className = '',
    isOnConf,
    id = 'banner',
}: StatusBannerProps): React.ReactElement | null => {
    if (!banner) return null;

    const {
        severity,
        message,
        persistent,
        displayCloseBtn,
        action,
        args,
        content,
        isDynamicMessage,
    } = banner;

    const needCloseBtn = Boolean(persistent || displayCloseBtn);
    const fadingState = 'appearing';

    const stringMessage = typeof message === 'string' ? message : '';

    const linesOfMessage = !isDynamicMessage && stringMessage ? stringMessage.split('\n') : [];

    const renderBanner = () => {
        if (isDynamicMessage) {
            return message;
        }

        return (
            <React.Fragment>
                {action ? (
                    <a
                        href='#'
                        className='toast-action'
                        onClick={seq(setAction(action, args, dispatch), closeCB)}>
                        {message}
                    </a>
                ) : (
                    linesOfMessage.map((line, index) => <div key={index}>{linkify(line)}</div>)
                )}
                {needCloseBtn ? <div className='closing-cross' onClick={closeCB} /> : null}
                {content}
            </React.Fragment>
        );
    };

    const renderSrOnly = () => {
        if (isDynamicMessage) {
            return null;
        }

        if (!stringMessage) {
            return null;
        }

        return (
            <span className='sr-only' role='alert' aria-live='assertive'>
                {action ? stringMessage : removeLinks(stringMessage)}
            </span>
        );
    };

    return (
        <div
            className={`${className} ${fadingState}${hasSidebar ? ' shifted' : ''} ${
                isOnConf ? 'on-conf' : ''
            }`}
            data-at='StatusBanner'
            id={id}>
            <div className={`toast ${fadingState} ${severity}`} data-at='Banner' tabIndex={-1}>
                {renderBanner()}
            </div>
            {renderSrOnly()}
        </div>
    );
};

StatusBanner.displayName = 'StatusBanner';

export default StatusBanner;
